import { useMemo } from "react"
import { stringifyExact } from "@/lib/lossless"
import { cn } from "@/lib/utils"

type Token = { readonly text: string; readonly kind: "key" | "string" | "number" | "literal" | "punct" | "space" }

const TOKEN = /("(?:\\.|[^"\\])*")(\s*:)?|(-?\d+(?:\.\d+)?(?:[eE][+-]?\d+)?)|(true|false|null)|([{}[\],:])|(\s+)/g

export function tokenize(json: string): Token[] {
  const out: Token[] = []
  for (const m of json.matchAll(TOKEN)) {
    if (m[1] !== undefined) {
      out.push({ text: m[1], kind: m[2] !== undefined ? "key" : "string" })
      if (m[2] !== undefined) out.push({ text: m[2], kind: "punct" })
    } else if (m[3] !== undefined) out.push({ text: m[3], kind: "number" })
    else if (m[4] !== undefined) out.push({ text: m[4], kind: "literal" })
    else if (m[5] !== undefined) out.push({ text: m[5], kind: "punct" })
    else if (m[6] !== undefined) out.push({ text: m[6], kind: "space" })
  }
  return out
}

const TONE: Record<Token["kind"], string> = {
  key: "text-foreground",
  string: "text-[oklch(0.8_0.09_160)] [[data-theme=light]_&]:text-[oklch(0.45_0.1_160)]",
  number: "text-[oklch(0.82_0.1_60)] [[data-theme=light]_&]:text-[oklch(0.5_0.12_55)]",
  literal: "text-primary",
  punct: "text-subtle-foreground",
  space: "",
}

/** Canonical JSON, read-only, with gutter line numbers; exact numbers keep their digits. Always left to right. */
export function JsonView({ value, className }: { value: unknown; className?: string }) {
  const lines = useMemo(() => {
    const text = stringifyExact(value, 2)
    return text.split("\n").map((line) => tokenize(line))
  }, [value])
  return (
    <div dir="ltr" className={cn("overflow-auto rounded-lg border border-border-subtle bg-card font-mono text-xs leading-5", className)}>
      <table className="w-full border-collapse">
        <tbody>
          {lines.map((tokens, i) => (
            <tr key={i}>
              <td className="w-10 select-none border-e border-border-subtle pe-2 text-end align-top text-subtle-foreground/70 tabular">{i + 1}</td>
              <td className="whitespace-pre ps-3 pe-4">
                {tokens.map((t, j) => (
                  <span key={j} className={TONE[t.kind]}>
                    {t.text}
                  </span>
                ))}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  )
}
